import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { Prayer } from '../types';

export type NotifSound = 'system' | 'adhan' | 'chime' | 'silent';

export interface NotificationPrefs {
  enabled: boolean;
  /** menit sebelum masuk waktu, mis. 0 = tepat waktu */
  beforeMinutes: number;
  /** true = jangan bunyikan sholat yang sudah dicentang */
  skipCompleted: boolean;
  /** system = bawaan HP, adhan = rekaman adzan, chime = nada in-app, silent = hening */
  sound: NotifSound;
}

export const DEFAULT_NOTIF_PREFS: NotificationPrefs = {
  enabled: false,
  beforeMinutes: 0,
  skipCompleted: true,
  sound: 'adhan',
};

/** Full adzan diputar saat notifikasi 'adhan' di web/PWA (native memakai file res/raw). */
const BASE = import.meta.env.BASE_URL || '/';
export const ADHAN_SHORT_URL = `${BASE}sounds/adhan-notification.mp3`;
export const ADHAN_FULL_URL = `${BASE}sounds/adhan-full.mp3`;

let currentAudio: HTMLAudioElement | null = null;

/** Putar rekaman adzan (short = potongan 27 dtk, full = adzan penuh). */
export function playAdhan(full = false): HTMLAudioElement {
  stopAdhan();
  const audio = new Audio(full ? ADHAN_FULL_URL : ADHAN_SHORT_URL);
  currentAudio = audio;
  audio.play().catch(() => {});
  return audio;
}

export function stopAdhan() {
  try {
    currentAudio?.pause();
    if (currentAudio) currentAudio.currentTime = 0;
  } catch { /* abaikan */ }
  currentAudio = null;
}

const IDS: Record<string, number> = {
  Fajr: 101,
  Dhuhr: 102,
  Asr: 103,
  Maghrib: 104,
  Isha: 105,
};

const CHANNEL_ID = 'adhan';
const CHANNEL_SILENT = 'adhan-silent';

function timeToDate(timeHHMM: string, offsetMin = 0): Date | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(timeHHMM.trim());
  if (!m) return null;
  const d = new Date();
  d.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  if (offsetMin) d.setMinutes(d.getMinutes() - offsetMin);
  return d;
}

export async function getPermissionStatus(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    if (Capacitor.isNativePlatform()) {
      const res = await LocalNotifications.checkPermissions();
      return res.display as any;
    }
    if ('Notification' in window) return Notification.permission as any;
  } catch { /* abaikan */ }
  return 'unknown';
}

export async function requestNotificationPermission(): Promise<boolean> {
  try {
    if (Capacitor.isNativePlatform()) {
      const res = await LocalNotifications.requestPermissions();
      return res.display === 'granted';
    }
    if ('Notification' in window) {
      const r = await Notification.requestPermission();
      return r === 'granted';
    }
  } catch (e) {
    console.log('Notif permission failed:', e);
  }
  return false;
}

async function ensureAndroidChannel(silent: boolean) {
  if (Capacitor.getPlatform() !== 'android') return;
  try {
    await LocalNotifications.createChannel({
      id: silent ? CHANNEL_SILENT : CHANNEL_ID,
      name: silent ? 'Pengingat Sholat (Hening)' : 'Pengingat Sholat',
      description: silent ? 'Notifikasi visual saja tanpa suara' : 'Notifikasi masuk waktu sholat',
      importance: silent ? 3 : 4,
      vibration: !silent,
    });
  } catch { /* abaikan */ }
}

/** Nada chime lembut via WebAudio (tanpa file aset). Untuk preview & fallback web. */
export async function playChime() {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx: AudioContext = new Ctx();
    const notes = [523.25, 659.25, 783.99]; // C5 E5 G5
    for (let i = 0; i < notes.length; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = notes[i];
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + i * 0.35);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + i * 0.35 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + i * 0.35 + 0.33);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.35);
      osc.stop(ctx.currentTime + i * 0.35 + 0.35);
    }
    setTimeout(() => ctx.close(), 1500);
  } catch { /* abaikan */ }
}

/** Jadwalkan notifikasi hari ini. Melewatkan waktu yang lewat + (opsional) yang sudah dicentang. */
export async function schedulePrayerNotifications(
  prayers: Pick<Prayer, 'name' | 'scheduledTime' | 'completed'>[],
  prefs: NotificationPrefs,
): Promise<number> {
  await cancelPrayerNotifications();

  if (!prefs.enabled) return 0;
  const ok = await requestNotificationPermission();
  if (!ok) throw new Error('Izin notifikasi belum diberikan');

  const now = new Date();
  const upcoming = prayers
    .filter((p) => !(prefs.skipCompleted && (p as any).completed))
    .map((p) => {
      const at = timeToDate(p.scheduledTime, prefs.beforeMinutes);
      return at ? { name: p.name, at } : null;
    })
    .filter((x): x is { name: string; at: Date } => !!x && x.at.getTime() > now.getTime() + 30_000);

  if (upcoming.length === 0) return 0;

  const silent = prefs.sound === 'silent';

  if (Capacitor.isNativePlatform()) {
    await ensureAndroidChannel(silent);
    const platform = Capacitor.getPlatform();
    await LocalNotifications.schedule({
      // Android: bunyi custom dari res/raw/adhan_notification.mp3
      // iOS: butuh 1 langkah manual di Xcode (lihat README): tambahkan
      //   adhan-notification.mp3 ke target App agar bisa dipakai sebagai sound.
      //   Sampai ditambahkan, iOS memakai bunyi sistem.
      notifications: upcoming.map(({ name, at }) => ({
        id: IDS[name] ?? Math.floor(Math.random() * 9000) + 200,
        title: `Waktunya Sholat ${name} 🕌`,
        body:
          prefs.beforeMinutes > 0
            ? `${prefs.beforeMinutes} menit lagi masuk waktu ${name}. Siapkan diri.`
            : `Sudah masuk waktu ${name}. Jangan tunda sholat.`,
        schedule: { at, allowWhileIdle: true },
        channelId: silent ? CHANNEL_SILENT : CHANNEL_ID,
        sound: prefs.sound === 'adhan' && platform === 'android' ? 'adhan_notification' : undefined,
        smallIcon: undefined,
      })),
    });
  } else {
    // Web/PWA: arm timer selama aplikasi terbuka (fallback, tidak jalan saat tab ditutup)
    armWebTimers(upcoming, prefs);
  }

  console.log('Scheduled prayer notifications:', upcoming.length);
  return upcoming.length;
}

export async function cancelPrayerNotifications() {
  disarmWebTimers();
  try {
    if (Capacitor.isNativePlatform()) {
      const pending = await LocalNotifications.getPending();
      const ours = pending.notifications.filter((n) =>
        Object.values(IDS).includes(n.id as number),
      );
      if (ours.length > 0) {
        await LocalNotifications.cancel({ notifications: ours.map((n) => ({ id: n.id })) });
      }
    }
  } catch { /* abaikan */ }
}

// ---- Web fallback: timer in-app ----
let webTimerIds: ReturnType<typeof setTimeout>[] = [];

function disarmWebTimers() {
  webTimerIds.forEach(clearTimeout);
  webTimerIds = [];
}

function armWebTimers(upcoming: { name: string; at: Date }[], prefs: NotificationPrefs) {
  disarmWebTimers();
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  for (const { name, at } of upcoming) {
    const delay = at.getTime() - Date.now();
    if (delay <= 0) continue;
    // setTimeout max ~24.8 hari, jadwal kita hanya hari ini jadi aman
    const id = setTimeout(() => {
      try {
        if (prefs.sound === 'chime') playChime();
        else if (prefs.sound === 'adhan') playAdhan(false);
        new Notification(`Waktunya Sholat ${name} 🕌`, {
          body:
            prefs.beforeMinutes > 0
              ? `${prefs.beforeMinutes} menit lagi masuk waktu ${name}.`
              : `Sudah masuk waktu ${name}.`,
          tag: `prayer-${name}`,
          silent: prefs.sound === 'silent',
        });
      } catch { /* abaikan */ }
    }, delay);
    webTimerIds.push(id);
  }
}
